/**
 * Audio resampling utilities for WebSocket transcription.
 * Converts browser audio (typically 44.1kHz/48kHz Float32) to 16kHz Int16 PCM
 * for the faster-whisper transcription server.
 */

export const TARGET_SAMPLE_RATE = 16000
export const SILENCE_THRESHOLD = 0.015
export const SILENCE_CHUNKS_BEFORE_PAUSE = 4

/**
 * Resample audio from source sample rate to target sample rate using linear interpolation.
 */
export function resampleAudio(
  inputBuffer: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number
): Float32Array {
  if (sourceSampleRate === targetSampleRate) {
    return inputBuffer
  }

  const ratio = sourceSampleRate / targetSampleRate
  const newLength = Math.round(inputBuffer.length / ratio)
  const result = new Float32Array(newLength)

  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio
    const srcIndexFloor = Math.floor(srcIndex)
    const srcIndexCeil = Math.min(srcIndexFloor + 1, inputBuffer.length - 1)
    const fraction = srcIndex - srcIndexFloor

    result[i] =
      (inputBuffer[srcIndexFloor] ?? 0) * (1 - fraction) +
      (inputBuffer[srcIndexCeil] ?? 0) * fraction
  }

  return result
}

/**
 * Convert Float32Array (-1.0 to 1.0) to Int16Array (-32768 to 32767).
 */
export function float32ToInt16(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length)
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i] ?? 0))
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return int16Array
}

/**
 * Calculate the RMS (root mean square) of an audio buffer.
 * Used for silence detection.
 */
export function calculateRMS(buffer: Float32Array): number {
  let sum = 0
  for (let i = 0; i < buffer.length; i++) {
    const sample = buffer[i] ?? 0
    sum += sample * sample
  }
  return Math.sqrt(sum / buffer.length)
}

/**
 * Convert Int16Array to hex string for WebSocket transmission.
 */
export function int16ToHex(int16Array: Int16Array): string {
  const bytes = new Uint8Array(int16Array.buffer)
  let hex = ''
  for (let i = 0; i < bytes.length; i++) {
    hex += (bytes[i] ?? 0).toString(16).padStart(2, '0')
  }
  return hex
}
